// [S-211] Template bulk upload — multi-file drag-drop with per-file LLM
// classification.
//
// Each dropped file is uploaded with ?autoTag=1, so the server extracts the
// content AND classifies it (deal type / guild / tier / anchor CBA) in one
// call. When the classifier is confident, the file is drafted automatically
// with those tags — no two files share the same metadata anymore. When it
// ISN'T confident (or classification errored), the row is held for the
// UPLOADER to review: pick the taxonomy inline, then save. This template
// review is deliberately SEPARATE from the deal (precedent) review queue —
// same idea, different surface.
//
// Layer is derived from the caller's role — platform_admin = L1, tenant_admin
// = L2 — same convention as TemplateAuthor (single-file path). Files are
// processed sequentially so a single failure doesn't poison the rest of the
// queue and audit log ordering stays deterministic.
import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../auth/AuthProvider";
import { TALENT_TIERS } from "../lib/talentTier";
import { Button, PageHead, Select } from "../components";
import { getApiErrorMessage } from "../data/api";
import { DriveImportButton } from "./DriveImportButton";
import { expandZipArchives } from "../data/fileIntake";
import {
  useSubmitL1Draft,
  useUploadL1Template,
  type TemplateTagSuggestion,
} from "../data/l1TemplatesQuery";
import {
  useSubmitL2Draft,
  useUploadL2Template,
  type L2TemplateRow,
} from "../data/l2TemplatesQuery";

// Below this the classifier isn't trusted to auto-draft — the uploader reviews
// the file's taxonomy before it becomes a draft.
const CONFIDENCE_THRESHOLD = 0.7;

type DealType = L2TemplateRow["dealType"];
type GuildCode = L2TemplateRow["guild"];
type Tier = L2TemplateRow["tier"];

type ReviewTags = {
  title: string | null;
  dealType: DealType;
  guild: GuildCode;
  tier: Tier;
  anchorCBA: string | null;
};

type FileStatus =
  | { state: "queued" }
  | { state: "uploading" }
  // Classifier wasn't confident — the uploader sets the taxonomy inline.
  | {
      state: "needs_review";
      uploadedId: string;
      content: string;
      tags: ReviewTags;
      confidence: number | null;
      reason: string;
    }
  | { state: "submitting" }
  | { state: "drafted"; uploadedId: string; auto: boolean; tags: ReviewTags }
  | { state: "failed"; message: string };

type QueueEntry = {
  /** Stable client-side id so React can key rows when filenames repeat. */
  key: string;
  file: File;
  status: FileStatus;
};

const DEAL_TYPES: ReadonlyArray<{ value: DealType; label: string }> = [
  { value: "feature_film", label: "Feature film" },
  { value: "tv_series", label: "TV series" },
  { value: "streaming", label: "Streaming" },
  { value: "documentary", label: "Documentary" },
  { value: "commercial", label: "Commercial" },
  { value: "short_form", label: "Short form" },
];

const GUILDS: ReadonlyArray<{ value: GuildCode; label: string }> = [
  { value: "SAG", label: "SAG-AFTRA" },
  { value: "DGA", label: "DGA" },
  { value: "WGA", label: "WGA" },
  { value: "IATSE", label: "IATSE" },
  { value: "AFM", label: "AFM" },
  { value: "PGA", label: "PGA" },
];

const TIERS: ReadonlyArray<{ value: Tier; label: string }> = [...TALENT_TIERS];

const ACCEPT_EXT = [".docx", ".pdf", ".txt", ".html", ".htm", ".md", ".markdown"];
// [S-527] Bare extensions (no dot) for zip-entry filtering during expansion.
const ACCEPT_EXT_SET: ReadonlySet<string> = new Set(
  ACCEPT_EXT.map((e) => e.slice(1))
);

// Best-guess when the classifier returns nothing at all — the uploader edits it.
const FALLBACK_TAGS: ReviewTags = {
  title: null,
  dealType: "feature_film",
  guild: "SAG",
  tier: "professional",
  anchorCBA: null,
};

function tagsFromSuggestion(s: TemplateTagSuggestion): ReviewTags {
  return {
    title: s.title,
    dealType: s.dealType,
    guild: s.guild,
    tier: s.tier,
    anchorCBA: s.anchorCBA,
  };
}

// Templates draft as effective-today; the effective date is confirmed at
// certification time, not per-file at bulk import.
function todayEffectiveIso(): string {
  return `${new Date().toISOString().slice(0, 10)}T00:00:00.000Z`;
}

// Uploaded filename minus its extension — the title fallback when the classifier
// returns none, so a template is always findable by the file the user uploaded.
function baseName(fileName: string): string {
  const trimmed = fileName.trim();
  const dot = trimmed.lastIndexOf(".");
  const stem = dot > 0 ? trimmed.slice(0, dot) : trimmed;
  return stem.length > 0 ? stem : trimmed;
}

export function TemplateBulkUpload(): JSX.Element {
  const session = useSession();
  const navigate = useNavigate();

  // Layer is SCOPE, not authz: platform staff (studioId null) author into
  // the L1 platform library; anyone with a studio into their L2 tenant
  // library. Screen access is gated at the route (RequireCapability
  // template:author), so no in-file redirect is needed here.
  const layer: "L1" | "L2" = session?.studioId == null ? "L1" : "L2";
  const tenantId =
    layer === "L2" && session?.studioId != null
      ? `tenant-${session.studioId}`
      : "";

  const uploadL1 = useUploadL1Template();
  const submitDraftL1 = useSubmitL1Draft();
  const uploadL2 = useUploadL2Template(tenantId);
  const submitDraftL2 = useSubmitL2Draft(tenantId);

  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [processing, setProcessing] = useState(false);
  // [S-527] Honest one-line summary after a drop/pick — how many queued, plus
  // any zip entries skipped (unsupported / unsafe) or truncated by the caps.
  const [notice, setNotice] = useState<string | null>(null);
  // webkitdirectory is a non-standard attribute React's types don't know
  // about — set it imperatively on mount (same pattern as the deals Upload).
  const folderInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    folderInputRef.current?.setAttribute("webkitdirectory", "");
  }, []);

  const counts = useMemo(() => {
    const c = {
      queued: 0,
      uploading: 0,
      needs_review: 0,
      submitting: 0,
      drafted: 0,
      failed: 0,
    };
    for (const q of queue) c[q.status.state] += 1;
    return c;
  }, [queue]);

  const addFiles = async (files: FileList | File[]): Promise<void> => {
    // [S-527] Expand any dropped/picked .zip archives client-side into their
    // supported entries first, then queue everything. Non-zip files pass
    // through untouched.
    const { files: expanded, skipped, truncated } = await expandZipArchives(
      Array.from(files),
      ACCEPT_EXT_SET
    );
    const incoming = expanded.filter((f) => {
      const ext = `.${(f.name.split(".").pop() ?? "").toLowerCase()}`;
      return ACCEPT_EXT.includes(ext);
    });
    if (incoming.length > 0) {
      const stamp = Date.now();
      setQueue((prev) => [
        ...prev,
        ...incoming.map((f, i) => ({
          key: `${stamp}-${prev.length + i}-${f.name}`,
          file: f,
          status: { state: "queued" as const },
        })),
      ]);
    }
    const parts: string[] = [];
    if (incoming.length > 0) {
      parts.push(`Added ${incoming.length} file${incoming.length === 1 ? "" : "s"}`);
    }
    if (skipped.length > 0) parts.push(`skipped ${skipped.length} unsupported`);
    if (truncated) parts.push("archive truncated at the size/count limit");
    setNotice(
      parts.length > 0
        ? parts.join(" · ") + "."
        : "No supported files found in that drop."
    );
  };

  const removeRow = (key: string): void => {
    setQueue((prev) => prev.filter((q) => q.key !== key));
  };

  const clearQueue = (): void => {
    if (processing) return;
    setQueue([]);
  };

  const updateRow = (key: string, status: FileStatus): void => {
    setQueue((prev) => prev.map((q) => (q.key === key ? { ...q, status } : q)));
  };

  // Persist a draft from a resolved classification (auto or reviewed).
  const submitDraft = async (
    rt: ReviewTags,
    uploadedId: string,
    content: string,
    fileName: string
  ): Promise<void> => {
    const payload = {
      id: uploadedId,
      // Always carry a human name: the classifier's title when it produced one,
      // else the uploaded filename (minus extension) — so the template is
      // findable by what the reviewer uploaded, never a bare id.
      title:
        rt.title !== null && rt.title.trim().length > 0
          ? rt.title
          : baseName(fileName),
      dealType: rt.dealType,
      guild: rt.guild,
      tier: rt.tier,
      anchorCBA: rt.anchorCBA,
      content,
      effectiveFrom: todayEffectiveIso(),
    };
    if (layer === "L1") await submitDraftL1.mutateAsync(payload);
    else await submitDraftL2.mutateAsync(payload);
  };

  // Upload + classify one file. Confident → draft it; unsure → hold for review.
  const uploadOne = async (entry: QueueEntry): Promise<void> => {
    updateRow(entry.key, { state: "uploading" });
    let result: Awaited<ReturnType<typeof uploadL1.mutateAsync>>;
    try {
      result =
        layer === "L1"
          ? await uploadL1.mutateAsync({ file: entry.file, autoTag: true })
          : await uploadL2.mutateAsync({ file: entry.file, autoTag: true });
    } catch (err) {
      updateRow(entry.key, {
        state: "failed",
        message: getApiErrorMessage(err) ?? errMsg(err, "upload failed"),
      });
      return;
    }

    const uploadedId = result.id;
    const content = result.draftContent;
    const tags = result.tags;
    const confident =
      tags !== undefined &&
      typeof tags.confidence === "number" &&
      tags.confidence >= CONFIDENCE_THRESHOLD;

    if (tags !== undefined && confident) {
      updateRow(entry.key, { state: "submitting" });
      const rt = tagsFromSuggestion(tags);
      try {
        await submitDraft(rt, uploadedId, content, entry.file.name);
        updateRow(entry.key, { state: "drafted", uploadedId, auto: true, tags: rt });
      } catch (err) {
        updateRow(entry.key, {
          state: "failed",
          message: getApiErrorMessage(err) ?? errMsg(err, "submit failed"),
        });
      }
      return;
    }

    // Not confident — the uploader reviews the taxonomy before it drafts.
    const rt = tags !== undefined ? tagsFromSuggestion(tags) : FALLBACK_TAGS;
    const reason =
      result.tagsError !== undefined
        ? "Couldn't auto-classify"
        : tags === undefined
          ? "No classification returned"
          : `Low confidence (${Math.round((tags.confidence ?? 0) * 100)}%)`;
    updateRow(entry.key, {
      state: "needs_review",
      uploadedId,
      content,
      tags: rt,
      confidence: tags?.confidence ?? null,
      reason,
    });
  };

  const processAll = async (): Promise<void> => {
    if (processing) return;
    setProcessing(true);
    try {
      // Snapshot the queued keys so files added mid-run don't sneak into this
      // pass (a later Process click handles those).
      const toProcess = queue.filter((q) => q.status.state === "queued");
      for (const entry of toProcess) {
        await uploadOne(entry);
      }
    } finally {
      setProcessing(false);
    }
  };

  // A needs_review row's inline taxonomy edit.
  const updateReviewTags = (key: string, patch: Partial<ReviewTags>): void => {
    setQueue((prev) =>
      prev.map((q) => {
        if (q.key !== key || q.status.state !== "needs_review") return q;
        return { ...q, status: { ...q.status, tags: { ...q.status.tags, ...patch } } };
      })
    );
  };

  const saveReviewed = async (key: string): Promise<void> => {
    const entry = queue.find((q) => q.key === key);
    if (entry === undefined || entry.status.state !== "needs_review") return;
    const { uploadedId, content, tags } = entry.status;
    updateRow(key, { state: "submitting" });
    try {
      await submitDraft(tags, uploadedId, content, entry.file.name);
      updateRow(key, { state: "drafted", uploadedId, auto: false, tags });
    } catch (err) {
      updateRow(key, {
        state: "failed",
        message: getApiErrorMessage(err) ?? errMsg(err, "submit failed"),
      });
    }
  };

  const pending =
    counts.queued + counts.uploading + counts.submitting + counts.needs_review;

  return (
    <>
      <PageHead
        eyebrow={layer === "L1" ? "Platform · import" : "Tenant · import"}
        title={
          <>
            Import <em>templates.</em>
          </>
        }
        sub={
          <>
            Drop files from your device, a folder, or Drive. Each is{" "}
            <b>auto-classified</b> (deal type, guild, tier) by the model — no
            shared defaults. Confident files draft automatically; anything the
            model isn't sure about waits for <b>you</b> to set the taxonomy
            inline. Refine further from{" "}
            <Link to="/template-management" className="tplbulk__inline-link">
              Template Management
            </Link>{" "}
            after they land.
          </>
        }
        cta={
          <Button
            variant="ghost"
            className="btn--s"
            onClick={() => navigate("/template-management")}
          >
            ← Back to management
          </Button>
        }
      />

      <section
        className="tplbulk__drop"
        onDrop={(e) => {
          e.preventDefault();
          if (e.dataTransfer?.files !== undefined) void addFiles(e.dataTransfer.files);
        }}
        onDragOver={(e: DragEvent<HTMLDivElement>) => e.preventDefault()}
        data-testid="tplbulk-drop"
      >
        <div className="tplbulk__drop__icon" aria-hidden="true">📁</div>
        <div className="tplbulk__drop__title">Drag and drop templates here</div>
        <div className="tplbulk__drop__hint">
          Or pick files from disk — DOCX, PDF, TXT, HTML — or a .zip of them.
          Each file is uploaded, named from its filename, and classified in one
          step — no separate action needed.
        </div>
        {/* Same source row as the deals upload: local files, a folder, and the
            connector sources in one place. Drive is L2/tenant-only (L1 platform
            templates have no tenant scope); SharePoint routes to the Settings
            connector — its OAuth isn't built yet, so honest, not faked. */}
        <div className="upl__drop__sources">
          <label className="upl__src upl__src--primary">
            <FileIcon />
            Browse files
            <input
              type="file"
              multiple
              accept={[...ACCEPT_EXT, ".zip"].join(",")}
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files !== null) void addFiles(e.target.files);
                e.target.value = ""; // allow re-selecting the same files
              }}
              data-testid="tplbulk-file-input"
            />
          </label>
          <label className="upl__src upl__src--ghost">
            <FolderIcon />
            Browse folder
            <input
              ref={folderInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files !== null) void addFiles(e.target.files);
                e.target.value = "";
              }}
              data-testid="tplbulk-folder-input"
            />
          </label>
          {layer === "L2" && tenantId !== "" && (
            <DriveImportButton
              tenantId={tenantId}
              target="templates"
              appearance="source"
            />
          )}
          <Link
            to="/settings#connectors"
            className="upl__src upl__src--ghost"
            data-testid="tplbulk-src-sharepoint"
          >
            <span
              className="upl__src__mark upl__src__mark--sharepoint"
              aria-hidden="true"
            >
              S
            </span>
            SharePoint
          </Link>
        </div>
      </section>

      {notice !== null && (
        <div
          className="tplbulk__notice"
          role="status"
          data-testid="tplbulk-intake-notice"
        >
          {notice}
        </div>
      )}

      {queue.length > 0 && (
        <section className="tplbulk__queue" data-testid="tplbulk-queue">
          <div className="tplbulk__queue__head">
            <div className="tplbulk__queue__counts">
              {queue.length} file{queue.length === 1 ? "" : "s"} ·{" "}
              {counts.drafted} drafted · {counts.needs_review} to review ·{" "}
              {counts.failed} failed · {pending - counts.needs_review} pending
            </div>
            <div className="tplbulk__queue__actions">
              <Button
                variant="ghost"
                className="btn--s"
                onClick={clearQueue}
                disabled={processing}
              >
                Clear all
              </Button>
              <Button
                onClick={() => void processAll()}
                disabled={processing || counts.queued === 0}
                data-testid="tplbulk-process"
              >
                {/* [S-641] Frame this as ONE upload step — upload + auto-classify
                    happen together — so it doesn't read as a separate, optional
                    "classify" chore (Bill deemed it unnecessary). */}
                {processing
                  ? "Uploading & classifying…"
                  : `Upload & classify ${counts.queued} file${counts.queued === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
          {counts.needs_review > 0 && !processing && (
            <div className="tplbulk__reviewbanner" role="status">
              <b>{counts.needs_review}</b> file
              {counts.needs_review === 1 ? "" : "s"} need your review — the model
              wasn't confident. Set the taxonomy below, then save each.
            </div>
          )}
          <ul className="tplbulk__rows">
            {queue.map((q) => (
              <QueueRow
                key={q.key}
                entry={q}
                onRemove={() => removeRow(q.key)}
                onOpenDraft={(id) =>
                  navigate(`/library/${encodeURIComponent(id)}/edit`)
                }
                onUpdateTags={(patch) => updateReviewTags(q.key, patch)}
                onSaveReviewed={() => void saveReviewed(q.key)}
                processing={processing}
              />
            ))}
          </ul>
          {counts.drafted > 0 && !processing && (
            <div className="tplbulk__done" role="status">
              <span>
                <b>{counts.drafted}</b> draft{counts.drafted === 1 ? "" : "s"}{" "}
                created.
              </span>
              <Button
                variant="secondary"
                className="btn--s"
                onClick={() => navigate("/template-management")}
              >
                View in management →
              </Button>
            </div>
          )}
        </section>
      )}
    </>
  );
}

function QueueRow({
  entry,
  onRemove,
  onOpenDraft,
  onUpdateTags,
  onSaveReviewed,
  processing,
}: {
  entry: QueueEntry;
  onRemove: () => void;
  onOpenDraft: (id: string) => void;
  onUpdateTags: (patch: Partial<ReviewTags>) => void;
  onSaveReviewed: () => void;
  processing: boolean;
}): JSX.Element {
  const s = entry.status;
  return (
    <li
      className={`tplbulk__row tplbulk__row--${s.state}`}
      data-testid={`tplbulk-row-${entry.key}`}
    >
      <div className="tplbulk__row__main">
        <span className="tplbulk__row__name">{entry.file.name}</span>
        <span className="tplbulk__row__size">{formatSize(entry.file.size)}</span>
        <span className="tplbulk__row__state">
          <StatusPill status={s} />
        </span>
        <span className="tplbulk__row__act">
          {s.state === "drafted" && (
            <>
              {s.auto && <TagChips tags={s.tags} />}
              <Button
                variant="ghost"
                className="btn--xs"
                onClick={() => onOpenDraft(s.uploadedId)}
              >
                Open draft →
              </Button>
            </>
          )}
          {s.state === "failed" && (
            <span className="tplbulk__row__err" title={s.message}>
              {s.message}
            </span>
          )}
          {s.state === "queued" && !processing && (
            <Button variant="bare" className="btn--xs" onClick={onRemove}>
              ×
            </Button>
          )}
        </span>
      </div>

      {s.state === "needs_review" && (
        <div className="tplbulk__review" data-testid={`tplbulk-review-${entry.key}`}>
          <div className="tplbulk__review__reason">{s.reason} — set the classification, then save.</div>
          <div className="tplbulk__review__fields">
            <label className="tplbulk__review__field">
              <span>Deal type</span>
              <Select
                label="Deal type"
                value={s.tags.dealType}
                options={DEAL_TYPES}
                clearable={false}
                onValueChange={(v) => v !== undefined && onUpdateTags({ dealType: v })}
              />
            </label>
            <label className="tplbulk__review__field">
              <span>Guild</span>
              <Select
                label="Guild"
                value={s.tags.guild}
                options={GUILDS}
                clearable={false}
                onValueChange={(v) => v !== undefined && onUpdateTags({ guild: v })}
              />
            </label>
            <label className="tplbulk__review__field">
              <span>Talent tier</span>
              <Select
                label="Talent tier"
                value={s.tags.tier}
                options={TIERS}
                clearable={false}
                onValueChange={(v) => v !== undefined && onUpdateTags({ tier: v })}
              />
            </label>
            <Button
              className="btn--s tplbulk__review__save"
              onClick={onSaveReviewed}
              data-testid={`tplbulk-review-save-${entry.key}`}
            >
              Save draft →
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function TagChips({ tags }: { tags: ReviewTags }): JSX.Element {
  const items = [prettyTag(tags.dealType), prettyGuild(tags.guild), prettyTag(tags.tier)];
  return (
    <span className="tplbulk__row__tags">
      {items.map((t) => (
        <span className="tplbulk__row__tag" key={t}>
          {t}
        </span>
      ))}
    </span>
  );
}

function StatusPill({ status }: { status: FileStatus }): JSX.Element {
  switch (status.state) {
    case "queued":
      return <span className="tplbulk__pill tplbulk__pill--queued">Queued</span>;
    case "uploading":
      return <span className="tplbulk__pill tplbulk__pill--running">Classifying…</span>;
    case "needs_review":
      return <span className="tplbulk__pill tplbulk__pill--review">Needs review</span>;
    case "submitting":
      return <span className="tplbulk__pill tplbulk__pill--running">Saving…</span>;
    case "drafted":
      return (
        <span className="tplbulk__pill tplbulk__pill--ok">
          {status.auto ? "Auto-classified" : "Drafted"}
        </span>
      );
    case "failed":
      return <span className="tplbulk__pill tplbulk__pill--err">Failed</span>;
  }
}

function prettyTag(v: string): string {
  return v.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function prettyGuild(g: string): string {
  return g === "SAG" ? "SAG-AFTRA" : g;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function errMsg(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  return fallback;
}

// Drop-zone source glyphs (sized by `.upl__src svg`) — mirrors the deals
// upload so the two source rows read identically.
function FileIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

function FolderIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
    </svg>
  );
}
